import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";

import {
  useEmployee,
  useEmployeeImage,
  useCreateEmployee,
  useUpdateEmployee,
  useUploadEmployeeImage,
  useCheckCompleteness,
} from "../../../api/queries/employeeQueries";
import {
  useCompanies,
  useBranchesByCompany,
  useDepartments,
  useJobTitles,
  useClients,
} from "../../../api/queries/organizationQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { EMPLOYEE_STATUSES } from "../../../utils/employeeStatus";

// Stable empty list so effects that depend on lookup data don't re-run every render.
const EMPTY = [];

const Section = ({ title, children, icon }) => (
  <div className="mb-4">
    <div className="d-flex align-items-center px-3 py-2 rounded-2 mb-3 bg-slate-800">
      <i className={`bi ${icon} text-white me-2 fs-085`}></i>
      <h6 className="m-0 text-white text-uppercase fs-075 ls-005em">{title}</h6>
    </div>
    <div className="row g-3 px-2">{children}</div>
  </div>
);

const CustomInput = ({
  label,
  name,
  value,
  onChange,
  disabled,
  type = "text",
  col = "4",
  required = false,
  error = false,
  readOnly = false,
}) => (
  <div className={`col-md-${col}`}>
    <label className="extra-small mb-1 text-muted text-uppercase fs-070">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value ?? ""}
      onChange={onChange}
      className={`form-control form-control-sm shadow-none ${error ? "border-danger" : ""} field-control ${disabled ? "is-readonly" : readOnly ? "is-locked" : ""} ${!disabled && error ? "has-error" : ""}`}
      disabled={disabled}
      readOnly={readOnly}
    />
  </div>
);

const EmployeeDetailCard = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const hasLoadedRef = useRef(false);

  const [isEditing, setIsEditing] = useState(!id);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
  const [preview, setPreview] = useState(
    "https://placehold.co/200x200?text=Avatar",
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    extension: "",
    employeeId: "",
    employeeStatus: "Active",
    companyId: "",
    branchId: "",
    departmentId: "",
    jobTitle: "",
    clientCode: "",
    assignmentType: "Internal",
    clientId: "",
    isSeasonal: false,
  });

  // Server data (shared with other components through the query cache)
  const { data: employee } = useEmployee(id);
  const { data: image } = useEmployeeImage(id);
  const { data: allOrgs = EMPTY } = useCompanies();
  const { data: allBranches = EMPTY } = useBranchesByCompany(form.companyId);
  const { data: departments = EMPTY } = useDepartments(form.companyId, {
    enabled: !!form.companyId,
  });
  const { data: jobTitlesList = EMPTY } = useJobTitles();
  const { data: clientList = EMPTY } = useClients();

  const createEmployee = useCreateEmployee();
  const updateEmployee = useUpdateEmployee();
  const uploadImage = useUploadEmployeeImage();
  const checkCompleteness = useCheckCompleteness();

  // Helper to handle dynamic styles for select elements
  // Field classes for view / edit / error states (see profile.css)
  const fieldClass = (name) =>
    `field-control ${!isEditing ? "is-readonly" : ""} ${isEditing && errors[name] ? "has-error" : ""}`;

  useEffect(() => {
    if (employee) {
      const matchedJobTitle = jobTitlesList.find(
        (jt) => jt.id === employee.jobTitleId,
      );

      setForm({
        ...employee,
        firstName: employee.firstName ?? "",
        middleName:
          employee.middleName === "string" || !employee.middleName
            ? ""
            : employee.middleName,
        lastName: employee.lastName ?? "",
        extension:
          employee.extension === "string" || !employee.extension
            ? ""
            : employee.extension,

        companyId: employee.companyId ?? "",
        branchId: employee.branchId ?? "",
        departmentId: employee.departmentId ?? "",
        clientId: employee.clientId ?? "",
        jobTitle: matchedJobTitle ? matchedJobTitle.title : "",
        clientCode: employee.client?.clientCode || "",
        assignmentType: employee.assignmentType ?? "Internal",
        employeeStatus: employee.employeeStatus ?? "Active",
        isSeasonal: employee.isSeasonal === true,
      });
      if (id && !hasLoadedRef.current) {
        hasLoadedRef.current = true;
        setIsEditing(false);
      }
    }
  }, [employee, id, jobTitlesList]);

  useEffect(() => {
    if (image?.imageUrl && !selectedFile) setPreview(image.imageUrl);
  }, [image, selectedFile]);

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  const validate = () => {
    let newErrors = {};
    if (!form.firstName) newErrors.firstName = true;
    if (!form.lastName) newErrors.lastName = true;
    if (!form.companyId) newErrors.companyId = true;
    if (!form.branchId) newErrors.branchId = true; // Added Branch requirement
    if (!form.jobTitle) newErrors.jobTitle = true; // Added Job Title requirement

    if (form.assignmentType === "Internal") {
      if (!form.departmentId) newErrors.departmentId = true;
    } else {
      if (!form.clientId) newErrors.clientId = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setStatusMsg({ text: "", type: "" });
    setForm((prev) => {
      let updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
        ...(name === "companyId" ? { departmentId: "", branchId: "" } : {}),
      };
      if (name === "clientId") {
        const client = clientList.find((c) => String(c.id) === String(value));
        updated.clientCode = client ? client.clientCode : "";
      }
      return updated;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) {
      setStatusMsg({ text: "Missing required fields.", type: "danger" });
      return;
    }

    setStatusMsg({ text: "", type: "" });

    const payload = {
      firstName: form.firstName,
      middleName: form.middleName || "",
      lastName: form.lastName,
      extension: form.extension || "",
      companyId: form.companyId,
      branchId: form.branchId,
      assignmentType: form.assignmentType,
      departmentId:
        form.assignmentType === "Internal" ? form.departmentId : null,
      clientId: form.assignmentType === "Deployed" ? form.clientId : null,
      isSeasonal: form.assignmentType === "Deployed" ? form.isSeasonal : false,
      jobTitleId:
        jobTitlesList.find((jt) => jt.title === form.jobTitle)?.id || null,
      remarks: form.remarks || "",
      employeeStatus: form.employeeStatus,
    };

    try {
      let result;
      let targetId = id;

      if (id) {
        result = await updateEmployee.mutateAsync({ id, data: payload });
      } else {
        result = await createEmployee.mutateAsync(payload);
        targetId = result.id;
        hasLoadedRef.current = false;
      }

      if (selectedFile) {
        const imageData = new FormData();
        imageData.append("file", selectedFile);
        await uploadImage.mutateAsync({
          employeeId: targetId,
          file: imageData,
        });
        setSelectedFile(null);
      }

      await checkCompleteness.mutateAsync(targetId);

      setStatusMsg({
        text: "Saved and validated successfully!",
        type: "success",
      });
      setIsEditing(false);

      if (!id && targetId) {
        navigate(`/employee/${targetId}`, { replace: true });
      }

      setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
    } catch (err) {
      setStatusMsg({
        text: getApiErrorMessage(err, "Error saving."),
        type: "danger",
      });
    }
  };

  const badgeClass = (() => {
    const s = (form.employeeStatus || "").toLowerCase();
    if (s === "active" || s === "complete")
      return "status-badge-modern--success";
    if (s === "incomplete" || s === "inactive")
      return "status-badge-modern--danger";
    return "";
  })();

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  return (
    <div className="container-fluid px-0">
      <div className="bg-white p-3 rounded-3 shadow-sm border mb-4 position-relative">
        <div className="d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mb-4 section-bar">
          <div className="d-flex align-items-center">
            <i className="bi bi-person-vcard-fill text-white me-2 fs-090"></i>
            <h6 className="m-0 text-white text-uppercase fs-075">
              Employee Info
            </h6>
          </div>
          <div className="d-flex align-items-center gap-3">
            {statusMsg.text && (
              <span className={`text-uppercase small text-${statusMsg.type}`}>
                {statusMsg.text}
              </span>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row g-0">
            <div className="col-md-auto mb-4 text-center border-end pe-4 w-260px">
              <div className="avatar-container avatar-container--lg mb-3">
                <img src={preview} alt="Avatar" className="avatar-circle" />
                {isEditing && (
                  <div
                    className="upload-badge"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <i className="bi bi-camera-fill"></i>
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept="image/*"
                onChange={handlePhotoChange}
              />

              <div className="mb-4">
                {!isEditing ? (
                  <div className={`status-badge-modern ${badgeClass}`}>
                    {form.employeeStatus}
                  </div>
                ) : (
                  <div className="text-start px-1">
                    <label className="extra-small mb-1 text-muted text-uppercase">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      name="employeeStatus"
                      className={`form-select form-select-sm shadow-none ${fieldClass("employeeStatus")}`}
                      value={form.employeeStatus}
                      onChange={handleChange}
                    >
                      {EMPLOYEE_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="text-start mb-3 px-1">
                <label className="extra-small mb-1 text-muted text-uppercase">
                  Employee ID
                </label>
                <div className="form-control border-0 shadow-none text-center fw-bold employee-id-box">
                  {form.employeeId ? (
                    <>
                      ID: {form.employeeId} - {form.branch?.BranchCode || "HQ"}
                    </>
                  ) : (
                    "NEW RECORD"
                  )}
                </div>
              </div>

              <div className="text-start mb-1 px-1">
                <label className="extra-small mb-1 text-muted text-uppercase">
                  Assignment Type <span className="text-danger">*</span>
                </label>
                <select
                  name="assignmentType"
                  className={`form-select form-select-sm shadow-none ${fieldClass("assignmentType")}`}
                  value={form.assignmentType}
                  onChange={handleChange}
                  disabled={!isEditing}
                >
                  <option value="Internal">Internal</option>
                  <option value="Deployed">Deployed</option>
                </select>
              </div>

              {form.assignmentType === "Deployed" && (
                <div className="text-start mb-3 px-1 d-flex align-items-center mt-2">
                  <input
                    type="checkbox"
                    id="isSeasonal"
                    name="isSeasonal"
                    className="form-check-input me-2"
                    checked={form.isSeasonal}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                  <label
                    htmlFor="isSeasonal"
                    className="extra-small text-muted text-uppercase m-0"
                  >
                    Seasonal Employee
                  </label>
                </div>
              )}
            </div>

            <div className="col ps-4">
              <Section title="Personal Information" icon="bi-person-circle">
                <CustomInput
                  label="First Name"
                  name="firstName"
                  required
                  value={form.firstName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  error={errors.firstName}
                />
                <CustomInput
                  label="Middle Name"
                  name="middleName"
                  value={form.middleName}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Last Name"
                  name="lastName"
                  required
                  value={form.lastName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  error={errors.lastName}
                />
                <CustomInput
                  label="Extension"
                  name="extension"
                  col="2"
                  value={form.extension}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </Section>

              <Section title="Employment Information" icon="bi-building">
                <div className="col-md-6">
                  <label className="extra-small mb-1 text-muted text-uppercase">
                    Company <span className="text-danger">*</span>
                  </label>
                  <select
                    name="companyId"
                    className={`form-select form-select-sm ${errors.companyId ? "border-danger" : ""} ${fieldClass("companyId")}`}
                    value={form.companyId}
                    onChange={handleChange}
                    disabled={!isEditing}
                  >
                    <option value="">Select Company</option>
                    {allOrgs.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.companyName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="extra-small mb-1 text-muted text-uppercase">
                    Branch <span className="text-danger">*</span>
                  </label>
                  <select
                    name="branchId"
                    className={`form-select form-select-sm ${errors.branchId ? "border-danger" : ""} ${fieldClass("branchId")}`}
                    value={form.branchId}
                    onChange={handleChange}
                    disabled={!isEditing}
                  >
                    <option value="">Select Branch</option>
                    {allBranches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.branchName || b.companyName}
                      </option>
                    ))}
                  </select>
                </div>

                {form.assignmentType === "Internal" ? (
                  <>
                    <div className="col-md-6">
                      <label className="extra-small mb-1 text-muted text-uppercase">
                        Department <span className="text-danger">*</span>
                      </label>
                      <select
                        name="departmentId"
                        className={`form-select form-select-sm ${errors.departmentId ? "border-danger" : ""} ${fieldClass("departmentId")}`}
                        value={form.departmentId}
                        onChange={handleChange}
                        disabled={!isEditing}
                      >
                        <option value="">Select Department</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.departmentName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="extra-small mb-1 text-muted text-uppercase">
                        Job Title <span className="text-danger">*</span>
                      </label>
                      <select
                        name="jobTitle"
                        className={`form-select form-select-sm ${errors.jobTitle ? "border-danger" : ""} ${fieldClass("jobTitle")}`}
                        value={form.jobTitle}
                        onChange={handleChange}
                        disabled={!isEditing}
                      >
                        <option value="">Select Job Title</option>
                        {jobTitlesList.map((jt) => (
                          <option key={jt.id} value={jt.title}>
                            {jt.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-md-6">
                      <label className="extra-small mb-1 text-muted text-uppercase">
                        Payroll Group <span className="text-danger">*</span>
                      </label>
                      <select
                        name="clientId"
                        className={`form-select form-select-sm ${errors.clientId ? "border-danger" : ""} ${fieldClass("clientId")}`}
                        value={form.clientId}
                        onChange={handleChange}
                        disabled={!isEditing}
                      >
                        <option value="">Select Payroll Group</option>
                        {clientList.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name || c.clientName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <CustomInput
                      label="Payroll Group Code"
                      name="clientCode"
                      value={form.clientCode}
                      onChange={handleChange}
                      disabled={!isEditing}
                      readOnly={true}
                      col="6"
                    />

                    {/* Shared row for Deployed */}
                    <div className="col-md-6">
                      <label className="extra-small mb-1 text-muted text-uppercase">
                        Job Title <span className="text-danger">*</span>
                      </label>
                      <select
                        name="jobTitle"
                        className={`form-select form-select-sm ${errors.jobTitle ? "border-danger" : ""} ${fieldClass("jobTitle")}`}
                        value={form.jobTitle}
                        onChange={handleChange}
                        disabled={!isEditing}
                      >
                        <option value="">Select Job Title</option>
                        {jobTitlesList.map((jt) => (
                          <option key={jt.id} value={jt.title}>
                            {jt.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </Section>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
});

export default EmployeeDetailCard;
